import React, { createContext, useState, useContext, useEffect } from 'react';

export type MenuLayout = 'flat' | 'tabs';

interface MenuLayoutContextType {
  layout: MenuLayout;
  setLayout: (layout: MenuLayout) => void;
  toggleLayout: () => void;
}

export const MenuLayoutContext = createContext<MenuLayoutContextType>({
  layout: 'flat',
  setLayout: () => {},
  toggleLayout: () => {},
});

export const useMenuLayout = () => useContext(MenuLayoutContext);

interface MenuLayoutProviderProps {
  children: React.ReactNode;
}

export const MenuLayoutProvider: React.FC<MenuLayoutProviderProps> = ({ children }) => {
  // Load layout preference from localStorage
  const [layout, setLayoutState] = useState<MenuLayout>(() => {
    const stored = localStorage.getItem('menuLayout');
    return (stored === 'flat' || stored === 'tabs') ? stored : 'flat'; // Default to flat
  });

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('menuLayout', layout);
  }, [layout]);

  const setLayout = (newLayout: MenuLayout) => {
    setLayoutState(newLayout);
  };

  const toggleLayout = () => {
    setLayoutState((prev) => (prev === 'flat' ? 'tabs' : 'flat'));
  };

  return (
    <MenuLayoutContext.Provider value={{ layout, setLayout, toggleLayout }}>
      {children}
    </MenuLayoutContext.Provider>
  );
};
